/**
 * The ITEMS stream's own state (spec 02 §7.3-§7.5): the two timed item slots, the bonus stage, the
 * yellow cross's strikes, the flood, the explosions. Plain data only (06 R5). Lives on the world as
 * `itemSystem`, made by `setupItems` (or on first use).
 */

import { SECOND_FRAMES } from './tuning';
import type { Monster, PlayerState } from '../types';

/** One of the C64's two item slots ($52/$53 the item, $5D/$5E its per-second timer, $59../$5B.. its place). */
export interface Slot {
  /** FOODS index (the food slot) or POWERUPS index (the power-up slot); -1 empty for good ($FF). */
  item: number;
  /** Bit 7 of $52/$53 clear: on screen. */
  visible: boolean;
  /** Seconds: at the first pass after it goes below 0 the slot shows (then 11 again), at the next it goes. */
  timer: number;
  /** VIC frame (as a bubble on the grid): 8 x tile + $14, 8 x tile + $2D. */
  x: number;
  y: number;
  /** Its `Item` in `world.items` while visible, else -1. */
  itemId: number;
}

/** The yellow cross (D_58FF, D_593F and software slots 0 and 1). */
export interface StrikeState {
  /** Strikes still to come (D_58FF). */
  passes: number;
  /** A strike is on screen (D_593F). */
  on: boolean;
  /** Its cell: column ($DC) and the C64's bubble row ($EE: tile row + 3). */
  col: number;
  row: number;
  /** Passes left of its vanishing after the last step; -1 while it moves. */
  vanish: number;
  /** Who took the cross (credited for the kills). */
  by: 0 | 1;
}

/** The potion bonus stage (D_2F7D .. D_3080). */
export interface BonusStage {
  potion: number;
  phase: 'draw' | 'run' | 'results';
  /** Frames left of 'draw' or 'results'. */
  frames: number;
  /** The stage's timer ($2A), frames to its next second, and whether it runs at all (TXTTAB was running). */
  seconds: number;
  secondFrames: number;
  timed: boolean;
  /** Items placed, items left (D_597F), each player's count (STREND). */
  total: number;
  left: number;
  counts: [number, number];
  /** The stage's items in `world.items`. */
  ids: number[];
  perfect: boolean;
}

export interface ItemSystem {
  /** Set once the level's slots have been set up. */
  ready: boolean;
  food: Slot;
  powerup: Slot;
  /** Frames to the next second of the per-second timers (the C64's TXTTAB). */
  secondFrames: number;
  /** `flow.frame` up to which the per-second timers have been counted. */
  frameMark: number;
  /** What was seen last tick: each player's state, the Baron being out, the level clear. */
  seenState: [PlayerState, PlayerState];
  seenBaron: boolean;
  cleared: boolean;
  /** Players under the power heart (the C64's state $18): the handler twice an I-tick, flashing. */
  heartOn: [boolean, boolean];
  strike: StrikeState;
  /** The flood: rows filled from the bottom (0..25), frames into it (-1: none), who took the cross. */
  flood: number;
  floodFrames: number;
  floodBy: 0 | 1;
  /** An explosion: 'lamp' or 'bomb'; I-ticks into it (after the flash). */
  blast: 'none' | 'lamp' | 'bomb';
  blastTicks: number;
  /** `flow.frame` until which the border flashes (a strike's start, an explosion, an umbrella or door). */
  flashUntil: number;
  bonus: BonusStage | null;
  /** The bonus stage's monsters, out of play until its end (D_1E2E cleared their slots). */
  stash: Monster[];
  /** The round ends in a clear after the bonus stage: the next food base comes from its timer. */
  bonusDone: boolean;
}

const emptySlot = (): Slot => ({ item: -1, visible: false, timer: 0, x: 0, y: 0, itemId: -1 });

export function initialItemSystem(): ItemSystem {
  return {
    ready: false, food: emptySlot(), powerup: emptySlot(), secondFrames: SECOND_FRAMES, frameMark: 0,
    seenState: ['out', 'out'], seenBaron: false, cleared: false, heartOn: [false, false],
    strike: { passes: 0, on: false, col: 0, row: 0, vanish: -1, by: 0 },
    flood: 0, floodFrames: -1, floodBy: 0, blast: 'none', blastTicks: 0, flashUntil: 0,
    bonus: null, stash: [], bonusDone: false,
  };
}
